import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import { zeroFilledDailySeries } from '@apps/web/features/admin_agent_notes/model/heatmap_scale';
import {
    DOMAIN_CHART_COLORS,
    DOMAIN_LABELS,
    SOURCE_CHART_COLORS,
    SOURCE_LABELS,
} from '@apps/web/features/admin_agent_notes/ui/note_status';
import { SourceLegend } from '@apps/web/features/admin_agent_notes/ui/org_volume_chart';
import { formatDateKey } from '@apps/web/lib/date_utils';
import { type AdminAgentNoteStatsResponse } from '@libraries/typescript/api_client';

const CHART_BLUE = 'var(--chart-1)';
// Failure-rate alert line; the same threshold flags the Failed KPI tile red.
const FAILURE_ALERT_THRESHOLD_PCT = 2;

function ChartTooltipFrame({
    children,
}: {
    readonly children: React.ReactNode;
}) {
    return (
        <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-sm">
            {children}
        </div>
    );
}

type DailyPoint = {
    readonly date: string;
    readonly label: string;
    readonly total: number;
    readonly failed: number;
    readonly failureRatePct: number | null;
};

function windowSeries(stats: AdminAgentNoteStatsResponse): Array<DailyPoint> {
    return zeroFilledDailySeries(
        stats.daily_counts,
        stats.window_start,
        stats.window_end,
    ).map((entry) => ({
        date: entry.date,
        label: formatDateKey(entry.date, ANALYTICS_TIMEZONE),
        total: entry.total,
        failed: entry.failed,
        failureRatePct:
            entry.total > 0 ? (100 * entry.failed) / entry.total : null,
    }));
}

export function FailureRateChart({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    const data = windowSeries(stats);
    return (
        <ResponsiveContainer width="100%" height={180}>
            <LineChart
                data={data}
                margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
            >
                <CartesianGrid
                    vertical={false}
                    stroke="var(--border)"
                    strokeDasharray="0"
                />
                <XAxis
                    dataKey="label"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="var(--muted-foreground)"
                    interval="preserveStartEnd"
                />
                <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="var(--muted-foreground)"
                    unit="%"
                />
                <RechartsTooltip
                    content={({ active, payload }) => {
                        const point = payload?.[0]?.payload as
                            | DailyPoint
                            | undefined;
                        if (!active || point === undefined) {
                            return null;
                        }
                        return (
                            <ChartTooltipFrame>
                                <p className="font-medium">{point.label}</p>
                                <p className="tabular-nums">
                                    {point.failureRatePct === null
                                        ? 'No notes'
                                        : `${point.failureRatePct.toFixed(1)}% failed (${point.failed}/${point.total})`}
                                </p>
                            </ChartTooltipFrame>
                        );
                    }}
                />
                <ReferenceLine
                    y={FAILURE_ALERT_THRESHOLD_PCT}
                    stroke="var(--destructive)"
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                    label={{
                        value: `${FAILURE_ALERT_THRESHOLD_PCT}% threshold`,
                        position: 'insideTopRight',
                        fontSize: 10,
                        fill: 'var(--muted-foreground)',
                    }}
                />
                <Line
                    isAnimationActive={false}
                    dataKey="failureRatePct"
                    stroke="var(--destructive)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

export function PipelineFunnel({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    const funnel = stats.funnel;
    const stages = [
        { label: 'Captured', count: funnel.captured },
        { label: 'Classified', count: funnel.classified },
        { label: 'Routed', count: funnel.routed },
        { label: 'Applied', count: funnel.applied },
    ];
    const captured = Math.max(1, funnel.captured);
    return (
        <div className="flex flex-col gap-2">
            {stages.map((stage, index) => (
                <div key={stage.label} className="flex flex-col gap-0.5">
                    <div className="flex items-baseline justify-between text-xs">
                        <span>{stage.label}</span>
                        <span className="tabular-nums">
                            {stage.count}
                            {index > 0 && funnel.captured > 0 && (
                                <span className="text-muted-foreground">
                                    {' '}
                                    ·{' '}
                                    {((100 * stage.count) / captured).toFixed(
                                        1,
                                    )}
                                    %
                                </span>
                            )}
                        </span>
                    </div>
                    <span className="bg-muted h-3.5 overflow-hidden rounded-sm">
                        <span
                            className="block h-full rounded-sm"
                            style={{
                                width: `${(100 * stage.count) / captured}%`,
                                background: CHART_BLUE,
                                opacity: 1 - index * 0.15,
                            }}
                        />
                    </span>
                </div>
            ))}
        </div>
    );
}

export function DomainMixChart({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    if (stats.domain_counts.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-xs">
                No routed items in the selected range.
            </p>
        );
    }
    const maxCount = Math.max(
        1,
        ...stats.domain_counts.map((entry) => entry.count),
    );
    return (
        <div className="flex flex-col gap-1.5">
            {stats.domain_counts.map((entry) => (
                <div
                    key={entry.domain}
                    className="flex items-center gap-2 text-xs"
                >
                    <span className="text-muted-foreground w-[150px] flex-none truncate">
                        {DOMAIN_LABELS[entry.domain]}
                    </span>
                    <span className="bg-muted h-3.5 flex-1 overflow-hidden rounded-sm">
                        <span
                            className="block h-full rounded-sm"
                            style={{
                                width: `${(100 * entry.count) / maxCount}%`,
                                background: DOMAIN_CHART_COLORS[entry.domain],
                            }}
                        />
                    </span>
                    <span className="w-10 flex-none text-right tabular-nums">
                        {entry.count}
                    </span>
                </div>
            ))}
        </div>
    );
}

export function DailyBySourceChart({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    const daySeries = zeroFilledDailySeries(
        stats.daily_counts,
        stats.window_start,
        stats.window_end,
    );
    const countsByDay = new Map<string, Partial<Record<string, number>>>();
    const activeSources: Array<
        (typeof stats.daily_source_counts)[number]['source']
    > = [];
    for (const entry of stats.daily_source_counts) {
        const day = countsByDay.get(entry.date) ?? {};
        day[entry.source] = entry.count;
        countsByDay.set(entry.date, day);
        if (!activeSources.includes(entry.source)) {
            activeSources.push(entry.source);
        }
    }
    const data = daySeries.map((entry) => ({
        date: entry.date,
        label: formatDateKey(entry.date, ANALYTICS_TIMEZONE),
        total: entry.total,
        ...countsByDay.get(entry.date),
    }));

    if (activeSources.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-xs">
                No notes in the selected range.
            </p>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <ResponsiveContainer width="100%" height={190}>
                <BarChart
                    data={data}
                    margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke="var(--border)"
                        strokeDasharray="0"
                    />
                    <XAxis
                        dataKey="label"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        stroke="var(--muted-foreground)"
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        stroke="var(--muted-foreground)"
                        allowDecimals={false}
                    />
                    <RechartsTooltip
                        cursor={{ fill: 'var(--muted)' }}
                        content={({ active, payload }) => {
                            const point = payload?.[0]?.payload as
                                | { label: string; total: number }
                                | undefined;
                            if (!active || point === undefined) {
                                return null;
                            }
                            const parts = (payload ?? [])
                                .filter((item) => Number(item.value) > 0)
                                .map(
                                    (item) =>
                                        `${SOURCE_LABELS[item.dataKey as keyof typeof SOURCE_LABELS]} ${String(item.value)}`,
                                );
                            return (
                                <ChartTooltipFrame>
                                    <p className="font-medium">{point.label}</p>
                                    <p className="tabular-nums">
                                        {point.total} notes
                                    </p>
                                    {parts.length > 0 && (
                                        <p className="text-muted-foreground">
                                            {parts.join(' · ')}
                                        </p>
                                    )}
                                </ChartTooltipFrame>
                            );
                        }}
                    />
                    {activeSources.map((source) => (
                        <Bar
                            isAnimationActive={false}
                            key={source}
                            dataKey={source}
                            stackId="day"
                            fill={SOURCE_CHART_COLORS[source]}
                            stroke="var(--card)"
                            strokeWidth={1}
                            maxBarSize={26}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <SourceLegend sources={activeSources} />
        </div>
    );
}

type HourlyPoint = {
    readonly hour: number;
    readonly label: string;
    readonly count: number;
};

function hourLabel(hour: number): string {
    if (hour === 0) {
        return '12a';
    }
    if (hour === 12) {
        return '12p';
    }
    return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

export function HourOfDayChart({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    const data: Array<HourlyPoint> = stats.hourly_counts.map((entry) => ({
        hour: entry.hour,
        label: hourLabel(entry.hour),
        count: entry.count,
    }));
    return (
        <ResponsiveContainer width="100%" height={180}>
            <BarChart
                data={data}
                margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
            >
                <CartesianGrid
                    vertical={false}
                    stroke="var(--border)"
                    strokeDasharray="0"
                />
                <XAxis
                    dataKey="label"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="var(--muted-foreground)"
                    interval={2}
                />
                <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    stroke="var(--muted-foreground)"
                    allowDecimals={false}
                />
                <RechartsTooltip
                    cursor={{ fill: 'var(--muted)' }}
                    content={({ active, payload }) => {
                        const point = payload?.[0]?.payload as
                            | HourlyPoint
                            | undefined;
                        if (!active || point === undefined) {
                            return null;
                        }
                        return (
                            <ChartTooltipFrame>
                                <p className="font-medium">
                                    {point.label} org-local
                                </p>
                                <p className="tabular-nums">
                                    {point.count} notes
                                </p>
                            </ChartTooltipFrame>
                        );
                    }}
                />
                <Bar
                    isAnimationActive={false}
                    dataKey="count"
                    fill={CHART_BLUE}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={22}
                />
            </BarChart>
        </ResponsiveContainer>
    );
}
